import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useInvoiceWithItems, useMarkInvoicePaid, type InvoiceItemRow } from "@/hooks/useInvoices";
import { usePropertyCleaningAgreement } from "@/hooks/usePropertyCleaningAgreement";
import { useProperty } from "@/hooks/useProperties";
import { useBillingProfile } from "@/hooks/useBillingProfile";
import { usePayeeConnectStatus } from "@/hooks/usePayeeConnectStatus";
import { usePayInvoice } from "@/hooks/usePayInvoice";
import { format, parseISO } from "date-fns";
import { DollarSign, Package, Receipt, FileText, CheckCircle, Download, Loader2, CreditCard, Copy } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { formatMoney } from "@/lib/formatMoney";

const PAYEE_CONNECT_MESSAGE =
  "Hey — to get paid through Stravo, open Stravo → Profile → Payouts → Connect Stripe.";

interface InvoiceDetailViewProps {
  invoiceId: string;
  propertyName?: string;
  onBack?: () => void;
  /** When false (team member), Pay invoice and Mark as paid are hidden. */
  canManagePaymentsOwnerOnly?: boolean;
}

export function InvoiceDetailView({ invoiceId, propertyName, onBack, canManagePaymentsOwnerOnly = true }: InvoiceDetailViewProps) {
  const { invoice, items, isLoading, refetch } = useInvoiceWithItems(invoiceId);
  const markPaid = useMarkInvoicePaid();
  const agreement = usePropertyCleaningAgreement(invoice?.property_id);
  const { data: property } = useProperty(invoice?.property_id);
  const { data: billingProfile } = useBillingProfile();
  const { data: payeeConnectStatus } = usePayeeConnectStatus(
    invoice?.payee_type as "cleaner" | "company" | undefined,
    invoice?.payee_id
  );
  const payeeConnectId = payeeConnectStatus?.stripe_connected_account_id ?? null;
  const payeeReady =
    payeeConnectStatus?.stripe_connect_onboarding_status === "complete" &&
    payeeConnectStatus?.stripe_connect_payouts_enabled === true;
  const payInvoice = usePayInvoice();

  const billingMode = (property as { billing_mode?: string } | null)?.billing_mode;
  const isTrackOnly = billingMode === "track_only" || billingMode === "self_managed";
  const paymentModeOnApp = (agreement as { payment_mode?: string } | null)?.payment_mode === "on_app";
  const canPayInApp =
    !isTrackOnly &&
    paymentModeOnApp &&
    invoice?.status !== "paid" &&
    (Number(invoice?.total_amount_cents) ?? 0) > 0 &&
    !!billingProfile?.default_payment_method_id &&
    !!payeeConnectId &&
    !!payeeReady;
  const payDisabledReason =
    !billingProfile?.default_payment_method_id
      ? "Add a payment method to pay via Stravo."
      : !payeeConnectId
        ? "Payee hasn't connected payouts yet. Ask them to connect Stripe in Account → Payouts."
        : !payeeReady
          ? "Payee hasn't connected payouts yet. Ask them to connect Stripe in Account → Payouts."
          : null;
  const showCopyPayeeMessage = !!payDisabledReason && (!payeeConnectId || !payeeReady);

  if (isLoading || !invoice) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {isLoading ? "Loading invoice…" : "Invoice not found."}
        </CardContent>
      </Card>
    );
  }

  const currency = (agreement as { currency?: string } | null)?.currency ?? "CAD";
  const cleaningItems = items.filter((i) => i.item_type === "cleaning");
  const taxItems = items.filter(
    (i) => i.item_type === "manual" && (i.meta as { kind?: string } | null)?.kind === "tax"
  );
  const reimbursementItems = items.filter(
    (i) => i.item_type === "inventory_reimbursement" || i.item_type === "cleaner_purchase"
  );

  const totalDisplayCents = items.reduce((sum, i) => sum + Number(i.amount_cents), 0);
  const subtotalPaidToPayeeCents =
    Number((invoice as { subtotal_amount_cents?: number | null }).subtotal_amount_cents) ||
    totalDisplayCents ||
    0;
  const processingFeeCents =
    paymentModeOnApp
      ? Number((invoice as { processing_fee_amount_cents?: number | null }).processing_fee_amount_cents) || 0
      : 0;
  const totalChargedToOwnerCents =
    paymentModeOnApp
      ? Number((invoice as { amount_to_charge_cents?: number | null }).amount_to_charge_cents) ||
        subtotalPaidToPayeeCents + processingFeeCents
      : 0;
  const alreadyPaidCents = reimbursementItems
    .filter((i) => i.amount_cents === 0 && i.meta?.original_amount_cents != null)
    .reduce((sum, i) => sum + Number(i.meta!.original_amount_cents), 0);

  const handlePrint = () => {
    window.print();
  };

  return (
    <Card className="print:shadow-none print:border print:border-border" id="invoice-print-area">
      <CardHeader className="pb-3 print:pb-2">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-5 h-5 shrink-0" />
            <CardTitle className="text-base sm:text-lg truncate">Invoice</CardTitle>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 no-print">
            <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={handlePrint}>
              <Download className="w-4 h-4 mr-2" />
              Print / Save as PDF
            </Button>
            {onBack && (
              <Button variant="ghost" size="sm" className="w-full sm:w-auto" onClick={onBack}>
                ← Back to payments
              </Button>
            )}
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          {propertyName && <span>{propertyName} — </span>}
          {format(parseISO(invoice.period_start), "MMM d, yyyy")} – {format(parseISO(invoice.period_end), "MMM d, yyyy")}
          {" · "}
          Payee: {invoice.payee_type} ({invoice.status})
        </p>
      </CardHeader>
      <CardContent className="p-0">
        {/* Mobile: stacked card layout */}
        <div className="md:hidden p-4 space-y-4">
          <div>
            <p className="font-medium text-sm flex items-center gap-2 text-muted-foreground mb-2">
              <DollarSign className="w-4 h-4" /> Cleaning Fees
            </p>
            {cleaningItems.length === 0 ? (
              <p className="text-sm text-muted-foreground pl-6">No cleaning fees</p>
            ) : (
              <ul className="space-y-2">
                {cleaningItems.map((line) => (
                  <li key={line.id} className="flex justify-between items-start gap-2 py-2 border-b border-border/50 last:border-0">
                    <span className="text-sm break-words flex-1 min-w-0">{line.description || "—"}</span>
                    <span className="text-sm font-medium shrink-0">{formatMoney(Number(line.amount_cents), currency)}</span>
                  </li>
                ))}
              </ul>
            )}
            {taxItems.length > 0 && (
              <div className="pt-2">
                <p className="text-xs text-muted-foreground pl-6">Tax</p>
                <ul className="space-y-2">
                  {taxItems.map((line) => (
                    <li key={line.id} className="flex justify-between items-start gap-2 py-2 border-b border-border/50 last:border-0">
                      <span className="text-sm break-words flex-1 min-w-0">{line.description || "Tax"}</span>
                      <span className="text-sm font-medium shrink-0">{formatMoney(Number(line.amount_cents), currency)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div>
            <p className="font-medium text-sm flex items-center gap-2 text-muted-foreground mb-2">
              <Package className="w-4 h-4" /><Receipt className="w-4 h-4" /> Inventory Reimbursements
            </p>
            {reimbursementItems.length === 0 ? (
              <p className="text-sm text-muted-foreground pl-6">No reimbursements</p>
            ) : (
              <ul className="space-y-2">
                {reimbursementItems.map((line) => {
                  const isAutopayZero = Number(line.amount_cents) === 0 && line.meta?.original_amount_cents != null;
                  const originalCents = line.meta?.original_amount_cents ?? 0;
                  const paidAt = line.meta?.paid_at as string | undefined;
                  return (
                    <li key={line.id} className="flex justify-between items-start gap-2 py-2 border-b border-border/50 last:border-0">
                      <span className="text-sm break-words flex-1 min-w-0">{line.description || "Reimbursement"}</span>
                      <span className="text-sm font-medium shrink-0 text-right">
                        {isAutopayZero ? (
                          <span className="text-muted-foreground text-xs">{formatMoney(0, currency)} (Autopay). Original: {formatMoney(originalCents, currency)}</span>
                        ) : (
                          formatMoney(Number(line.amount_cents), currency)
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {paymentModeOnApp ? (
            <div className="border-t-2 pt-3 space-y-1">
              <div className="flex justify-between font-medium">
                <span className="text-sm">Subtotal (paid to payee)</span>
                <span>{formatMoney(subtotalPaidToPayeeCents, currency)}</span>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Processing fee (paid by owner)</span>
                <span>{formatMoney(processingFeeCents, currency)}</span>
              </div>
              <div className="flex justify-between font-medium">
                <span className="text-sm">Total charged to owner</span>
                <span>{formatMoney(totalChargedToOwnerCents, currency)}</span>
              </div>
              <p className="text-xs text-muted-foreground pt-1">
                Owners cover processing fees so cleaners receive the full amount owed.
              </p>
            </div>
          ) : (
            <div className="border-t-2 pt-3 flex justify-between font-medium">
              <span className="text-sm">Total (due)</span>
              <span>{formatMoney(totalDisplayCents, currency)}</span>
            </div>
          )}
          {alreadyPaidCents > 0 && (
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Already paid (autopay)</span>
              <span>{formatMoney(alreadyPaidCents, currency)}</span>
            </div>
          )}
        </div>
        <div className="hidden md:block overflow-x-auto w-full min-w-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[55%]">Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Section 1: Cleaning Fees */}
              <TableRow className="bg-muted/40">
                <TableCell colSpan={2} className="font-medium py-2">
                  <span className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Cleaning Fees
                  </span>
                </TableCell>
              </TableRow>
              {cleaningItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} className="text-sm text-muted-foreground">
                    No cleaning fees
                  </TableCell>
                </TableRow>
              ) : (
                cleaningItems.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell className="text-sm">{line.description || "—"}</TableCell>
                    <TableCell className="text-right text-sm font-medium">
                      {formatMoney(Number(line.amount_cents), currency)}
                    </TableCell>
                  </TableRow>
                ))
              )}
              {taxItems.length > 0 && (
                <>
                  <TableRow className="bg-muted/20">
                    <TableCell colSpan={2} className="text-sm font-medium">
                      Tax
                    </TableCell>
                  </TableRow>
                  {taxItems.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell className="text-sm">{line.description || "Tax"}</TableCell>
                      <TableCell className="text-right text-sm font-medium">
                        {formatMoney(Number(line.amount_cents), currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}

              {/* Section 2: Inventory Reimbursements */}
              <TableRow className="bg-muted/40">
                <TableCell colSpan={2} className="font-medium py-2">
                  <span className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    <Receipt className="w-4 h-4" />
                    Inventory Reimbursements
                  </span>
                </TableCell>
              </TableRow>
              {reimbursementItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} className="text-sm text-muted-foreground">
                    No reimbursements
                  </TableCell>
                </TableRow>
              ) : (
                reimbursementItems.map((line) => {
                  const isAutopayZero =
                    Number(line.amount_cents) === 0 &&
                    line.meta?.original_amount_cents != null;
                  const originalCents = line.meta?.original_amount_cents ?? 0;
                  const paidAt = line.meta?.paid_at as string | undefined;
                  return (
                    <TableRow key={line.id}>
                      <TableCell className="text-sm">
                        {line.description || "Reimbursement"}
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium">
                        {isAutopayZero ? (
                          <span className="text-muted-foreground">
                            {formatMoney(0, currency)} — Paid on {paidAt ? format(parseISO(paidAt), "MMM d, yyyy") : "—"} (Autopay). Original: {formatMoney(originalCents, currency)}
                          </span>
                        ) : (
                          formatMoney(Number(line.amount_cents), currency)
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}

              {/* Totals */}
              {paymentModeOnApp ? (
                <>
                  <TableRow className="border-t-2 font-medium">
                    <TableCell className="text-sm">Subtotal (paid to payee)</TableCell>
                    <TableCell className="text-right">
                      {formatMoney(subtotalPaidToPayeeCents, currency)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="text-sm text-muted-foreground">
                      Processing fee (paid by owner)
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {formatMoney(processingFeeCents, currency)}
                    </TableCell>
                  </TableRow>
                  <TableRow className="font-medium">
                    <TableCell className="text-sm">Total charged to owner</TableCell>
                    <TableCell className="text-right">
                      {formatMoney(totalChargedToOwnerCents, currency)}
                    </TableCell>
                  </TableRow>
                  <TableRow className="bg-muted/20">
                    <TableCell colSpan={2} className="text-xs text-muted-foreground">
                      Owners cover processing fees so cleaners receive the full amount owed.
                    </TableCell>
                  </TableRow>
                </>
              ) : (
                <TableRow className="border-t-2 font-medium">
                  <TableCell className="text-sm">Total (due)</TableCell>
                  <TableCell className="text-right">
                    {formatMoney(totalDisplayCents, currency)}
                  </TableCell>
                </TableRow>
              )}
              {alreadyPaidCents > 0 && (
                <TableRow className="bg-muted/30">
                  <TableCell className="text-sm text-muted-foreground">
                    Already paid (autopay)
                  </TableCell>
                  <TableCell className="text-right text-sm text-muted-foreground">
                    {formatMoney(alreadyPaidCents, currency)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pay invoice (on-app) or Mark as paid (off-app) — owner only */}
        <div className="p-4 border-t space-y-3 no-print">
          {!canManagePaymentsOwnerOnly && invoice.status !== "paid" && (
            <p className="text-sm text-muted-foreground">Only the owner can pay or mark this invoice as paid.</p>
          )}
          {canManagePaymentsOwnerOnly && !isTrackOnly && paymentModeOnApp && invoice.status !== "paid" && (Number(invoice.total_amount_cents) ?? 0) > 0 && (
            <>
              {canPayInApp ? (
                <Button
                  onClick={async () => {
                    try {
                      await payInvoice.mutateAsync(invoiceId);
                      refetch?.();
                      toast({ title: "Paid" });
                    } catch (err: unknown) {
                      toast({
                        title: "Payment failed",
                        description: err instanceof Error ? err.message : "Could not pay invoice",
                        variant: "destructive",
                      });
                    }
                  }}
                  disabled={payInvoice.isPending}
                >
                  {payInvoice.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  Pay invoice
                </Button>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {payDisabledReason ?? "Unable to pay this invoice."}
                  </p>
                  {showCopyPayeeMessage && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        await navigator.clipboard.writeText(PAYEE_CONNECT_MESSAGE);
                        toast({ title: "Message copied" });
                      }}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy message
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
          {canManagePaymentsOwnerOnly && !paymentModeOnApp && (
            <p className="text-sm text-muted-foreground">
              Payment handled outside Stravo. Use &quot;Mark as paid&quot; when you have paid the payee.
            </p>
          )}
          {canManagePaymentsOwnerOnly && !paymentModeOnApp && invoice.status !== "paid" && (
            <Button
              onClick={() => markPaid.mutate(invoiceId)}
              disabled={markPaid.isPending}
            >
              {markPaid.isPending ? "Updating…" : <><CheckCircle className="w-4 h-4 mr-2" /> Mark as paid</>}
            </Button>
          )}
          {invoice.status === "paid" && (
            <p className="text-sm font-medium text-green-600">Paid</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
